import React, { FC, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { IndexAreaLoadingSpinner } from 'buyside/components/presentation/IndexPage/IndexPage';
import IndexPageHeader from 'buyside/components/presentation/IndexPageHeader/indexPageHeader';
import IndexPaginationNav from 'buyside/components/presentation/IndexPaginationNav/indexPaginationNav';
import ResearchItemLoading from 'buyside/components/presentation/ResearchItem/researchItemLoading';
import ResearchItem, {
  ResearchTypes
} from 'buyside/components/presentation/ResearchItem/researchItem';
import WhitePaperIndexPageFilter, {
  renderWhitePaperSideButton,
  renderWhitePaperSortOrders
} from 'buyside/components/presentation/WhitePaper/IndexPage/WhitePaperIndexPageFilter';

import { validatePageParam } from 'buyside/util/numbers';
import { BREAKPOINTS, useIsMobile } from 'buyside/util/hooks/useIsMobile';
const { indexTypes } = require('buyside/util/constantsFromRails.json');

import { useWhitePapersIndexListQuery } from 'interfaces/graphql-typings';

import styles from './styles.scss';

const WhitePaperIndexPage: FC = () => {
  const [searchParams] = useSearchParams();
  validatePageParam(searchParams);

  const { data, loading, error, previousData } = useWhitePapersIndexListQuery({
    variables: {
      searchParams: searchParams.toString(),
      indexType: indexTypes.white_paper
    }
  });

  const whitePaperIndexScrollToTop = (): void =>
    document?.getElementById('whitePaperIndexPageHeader')?.scrollIntoView();

  const canWriteWhitePapers = data?.currentUser?.canWriteWhitePapers;
  const indexListResultCount = data?.indexListResultCount || 0;

  const renderFilters = useCallback(() => <WhitePaperIndexPageFilter />, []);
  const renderSideButtons = useCallback(
    () => renderWhitePaperSideButton(Boolean(canWriteWhitePapers)),
    [canWriteWhitePapers]
  );
  const renderSortOrders = useCallback(() => renderWhitePaperSortOrders(), []);

  return (
    <main className={styles.main}>
      <div className={`${styles.indexPageContainer}`}>
        <div className={styles.mainIndexPageArea} id="whitePaperIndexPageHeader">
          <IndexPageHeader
            header="Macro / Thematic Research"
            renderFilters={renderFilters}
            renderSideButtons={renderSideButtons}
            renderSortOrders={renderSortOrders}
            resultCount={indexListResultCount}
            showMoreLess={useIsMobile(BREAKPOINTS.LARGE)}
          />
          {error && <div className={styles.error}>{error.message}</div>}
          <IndexAreaLoadingSpinner loading={loading} data={data || previousData}>
            {loading && !(data || previousData) && <ResearchItemLoading times={3} />}
            {(data || previousData)?.whitePapersIndexList?.map(whitePaper => (
              <ResearchItem
                {...whitePaper}
                key={whitePaper.id}
                researchType={ResearchTypes.WHITE_PAPER}
                showUserDetails={Boolean(data?.currentUser?.canViewUserDetails)}
              />
            ))}
          </IndexAreaLoadingSpinner>
          <IndexPaginationNav
            customScroll={whitePaperIndexScrollToTop}
            resultCount={indexListResultCount}
            resultLength={data?.whitePapersIndexList?.length || 0}
            resultsPerPage={10}
          />
        </div>
      </div>
    </main>
  );
};
export default WhitePaperIndexPage;
